import React, { useState } from "react";
import styled from "styled-components";
import { LuArrowRight, LuCheck, LuSparkles } from "react-icons/lu";

import brk from "../../util/breakpoints";
import interests from "../../data/interests";
import Button from "../Button";
import Modal, { Dismiss } from "../Modal";

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const HeaderCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  h2 {
    color: var(--text);
    font-family: var(--fontHeading);
    font-size: 36px;
    font-weight: 700;
    @media ${brk.device.tablet} {
      font-size: 26px;
    }
  }
  p {
    color: var(--textMuted);
    font-family: var(--fontBody);
    font-size: 16px;
    line-height: 1.5;
  }
`;

const OptionalLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--lime);
  font-family: var(--fontMono);
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--lime);
  }
`;

const Options = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  @media ${brk.device.tablet} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Option = styled.button`
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 116px;
  padding: 20px;
  border: 1px solid var(--border);
  border-radius: 14px;
  outline: ${(props) => (props.$selected ? "2px solid var(--purple)" : "1px solid transparent")};
  outline-offset: ${(props) => (props.$selected ? "-2px" : "-1px")};
  background: ${(props) => (props.$selected ? "var(--purpleSurface)" : "var(--card)")};
  text-align: left;
  cursor: pointer;
  transition: background 0.2s, outline-color 0.2s;
  &:hover {
    outline-color: ${(props) => (props.$selected ? "var(--purple)" : "var(--borderStrong)")};
  }
  &:focus-visible {
    box-shadow: 0 0 0 4px rgba(242, 244, 234, 0.3);
  }
  @media ${brk.device.tablet} {
    min-height: 0;
    padding: 16px;
  }
`;

const OptionCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
  h3 {
    color: var(--text);
    font-family: var(--fontHeading);
    font-size: 20px;
    font-weight: 700;
  }
  p {
    color: var(--textMuted);
    font-family: var(--fontBody);
    font-size: 12px;
    line-height: 1.45;
  }
`;

const SelectionControl = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  outline: 1px solid ${(props) => (props.$selected ? "var(--purple)" : "var(--borderStrong)")};
  outline-offset: -1px;
  background: ${(props) => (props.$selected ? "var(--purple)" : "transparent")};
  color: var(--text);
  transition: background 0.2s;
  &::after {
    content: "";
    display: ${(props) => (props.$selected ? "none" : "block")};
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: var(--borderStrong);
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  @media ${brk.device.tablet} {
    flex-direction: column-reverse;
    align-items: stretch;
    a,
    button {
      justify-content: center;
    }
  }
`;

const PrivacyNote = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--textDim);
  font-family: var(--fontMono);
  font-size: 9px;
  svg {
    flex-shrink: 0;
    color: var(--purpleText);
  }
`;

function TailorModal(props) {
  const { interest, onSelect, onClose } = props;

  const [selected, setSelected] = useState(interest || interests[0].id);

  return (
    <Modal onClose={onClose} labelledBy="tailor-title">
      <Header>
        <HeaderCopy>
          <OptionalLabel>
            <span />
            Optional · you can skip this
          </OptionalLabel>
          <h2 id="tailor-title">Let me tailor this for you.</h2>
          <p>What are you most interested in?</p>
        </HeaderCopy>
        <Dismiss onClick={onClose} />
      </Header>
      <Options role="radiogroup" aria-labelledby="tailor-title">
        {interests.map((option) => {
          const isSelected = selected === option.id;
          return (
            <Option
              key={option.id}
              role="radio"
              aria-checked={isSelected}
              $selected={isSelected}
              onClick={() => {
                setSelected(option.id);
              }}
            >
              <OptionCopy>
                <h3>{option.title}</h3>
                <p>{option.description}</p>
              </OptionCopy>
              <SelectionControl $selected={isSelected}>
                {isSelected && <LuCheck size={14} />}
              </SelectionControl>
            </Option>
          );
        })}
      </Options>
      <Footer>
        <PrivacyNote>
          <LuSparkles size={14} />
          NO ACCOUNT · NO TRACKING · CHANGE ANYTIME
        </PrivacyNote>
        <Button
          onClick={() => {
            onSelect(selected);
          }}
          icon={<LuArrowRight size={16} />}
        >
          Show me relevant work
        </Button>
      </Footer>
    </Modal>
  );
}

export default TailorModal;
